import {
  build_case_form_sections,
  build_stage_timeline,
  build_summary_rows,
  hydrate_case_detail
} from '@/services/onboarding_journey_service';
import {
  build_compliance_panel_state,
  evaluate_case_compliance
} from '@/services/onboarding_compliance_service';

const QUEUE_STATUS_TONE_RULES = [
  { tone: 'submitted', matches: ({ label }) => label.includes('submitted') },
  { tone: 'documents', matches: ({ label }) => label.includes('document') },
  { tone: 'manual', matches: ({ label }) => label.includes('manual') },
  { tone: 'verification', matches: ({ label }) => label.includes('verification') },
  { tone: 'risk', matches: ({ label }) => label.includes('risk') },
  { tone: 'approved', matches: ({ label }) => label.includes('approved') || label.includes('approve') },
  { tone: 'rejected', matches: ({ label }) => label.includes('rejected') || label.includes('reject') },
  { tone: 'pending', matches: ({ label }) => label.includes('pending') },
  { tone: 'intake', matches: ({ label }) => label.includes('intake') },
  { tone: 'activated', matches: ({ label }) => label.includes('active') || label.includes('activated') },
  { tone: 'complete', matches: ({ label, status }) => ['complete', 'completed', 'ready'].some((word) => label.includes(word)) || status === 'success' },
  { tone: 'blocked', matches: ({ label, status }) => ['blocked', 'failed', 'expired', 'overdue'].some((word) => label.includes(word)) || status === 'danger' },
  { tone: 'in-progress', matches: ({ label, status }) => label.includes('progress') || status === 'warning' }
];

function resolve_case_esigning_context(vm, target_case) {
  const esigning_context = {
    entity_profile_id: Number(
      vm.first_defined_value([
        target_case?.entity_profile_id,
        target_case?.entity_profile?.id,
        target_case?.profile?.entity_profile_id,
        target_case?.profile?.id
      ], 0)
    ),
    entity_type_id: Number(
      vm.first_defined_value([
        target_case?.entity_type_id,
        target_case?.entity_type?.id,
        target_case?.current_entity_type?.id
      ], 0)
    ),
    application_id: Number(vm.first_defined_value([target_case?.application_id], 0))
  };
  return esigning_context;
}

function build_esigning_route_query(context) {
  const query = {
    entity_profile_id: String(context.entity_profile_id),
    entity_type_id: String(context.entity_type_id),
    startNewSigning: '1'
  };
  if (context.application_id) {
    query.application_id = String(context.application_id);
  }
  return query;
}

function dispatch_system_loader(message) {
  window.dispatchEvent(new CustomEvent('system-loader:show', {
    detail: message
  }));
}

function clear_system_loader() {
  window.dispatchEvent(new CustomEvent('system-loader:hide'));
}

function get_target_case(case_item, selected_case) {
  const target_case = case_item || selected_case;
  return target_case;
}

function has_esigning_context(context = {}) {
  const is_valid = Boolean(context.entity_profile_id && context.entity_type_id);
  return is_valid;
}

function build_esigning_route(context) {
  const route = context.application_id
    ? {
      name: 'OnboardingEsigningCase',
      params: {
        applicationId: String(context.application_id)
      },
      query: build_esigning_route_query(context)
    }
    : {
      name: 'OnboardingEsigningSetup',
      query: build_esigning_route_query(context)
    };
  return route;
}

function queue_item_matches_case(item = {}, next_case = {}) {
  const item_application_id = String(item?.case_item?.application_id || '');
  const next_application_id = String(next_case?.application_id || '');
  return item_application_id === next_application_id;
}

function build_compliance_queue_item(item = {}, next_case = {}) {
  const next_item = {
    ...item,
    gate: next_case?.workflow_display_stage || item?.gate,
    case_item: next_case
  };
  return next_item;
}

function can_run_case_compliance(vm) {
  return Boolean(vm.selected_case && !vm.compliance_action_key);
}

function start_case_compliance(vm) {
  vm.compliance_action_key = `compliance:${vm.selected_case.application_id || ''}`;
  vm.compliance_error = '';
  dispatch_system_loader('Evaluating compliance...');
}

async function execute_case_compliance(vm) {
  const evaluation_result = await evaluate_case_compliance(vm.selected_case);
  const hydrated_case = await hydrate_case_detail(vm.selected_case);
  vm.apply_compliance_case_update(hydrated_case, evaluation_result);
  window.dispatchEvent(new CustomEvent('onboarding-data-updated'));
}

function finish_case_compliance(vm) {
  vm.compliance_action_key = '';
  clear_system_loader();
}

function set_case_compliance_error(vm, error) {
  vm.compliance_error = error?.response?.data?.message || error?.message || 'Unable to evaluate compliance.';
}

export const myQueueStatusMethods = {
  launch_esigning_for_case(case_item, event) {
    event?.stopPropagation?.();

    const target_case = get_target_case(case_item, this.selected_case);
    if (!target_case) return;
    const esigning_context = resolve_case_esigning_context(this, target_case);

    if (!has_esigning_context(esigning_context)) {
      this.queue_error = 'This onboarding is missing entity profile context for e-signing.';
      return;
    }

    const route = build_esigning_route(esigning_context);
    this.$router.push(route);
  },
  async run_selected_case_compliance() {
    if (!can_run_case_compliance(this)) return;
    start_case_compliance(this);

    try {
      await execute_case_compliance(this);
    } catch (error) {
      set_case_compliance_error(this, error);
    } finally {
      finish_case_compliance(this);
    }
  },
  apply_compliance_case_update(hydrated_case, evaluation_result) {
    this.selected_case = this.apply_persisted_edits_to_case(hydrated_case);
    this.summary_rows = build_summary_rows(this.selected_case);
    this.compliance_panel = build_compliance_panel_state(this.selected_case, evaluation_result);
    this.stage_timeline = build_stage_timeline(this.selected_case);
    this.case_form_sections = build_case_form_sections(this.selected_case);
    this.detail_tabs = this.build_detail_tabs(this.case_form_sections);
    this.queue_items = this.queue_items.map((item) => this.replace_queue_item_after_compliance(item, this.selected_case));
  },
  replace_queue_item_after_compliance(item, next_case) {
    return queue_item_matches_case(item, next_case)
      ? build_compliance_queue_item(item, next_case)
      : item;
  },
  compliance_tone_class(tone = '') {
    if (tone === 'success') return 'bg-success-subtle text-success-emphasis';
    if (tone === 'warning') return 'bg-warning-subtle text-warning-emphasis';
    if (tone === 'danger') return 'bg-danger-subtle text-danger-emphasis';
    return 'bg-secondary-subtle text-secondary-emphasis';
  },
  compliance_rule_icon(rule = {}) {
    if (rule.tone === 'success') return 'bi-check-circle';
    if (rule.tone === 'warning') return 'bi-exclamation-triangle';
    if (rule.tone === 'danger') return 'bi-x-octagon';
    return 'bi-circle';
  },
  status_icon(status) {
    return status === 'success' ? 'bi-check-circle' : 'bi-exclamation-circle';
  },
  queue_status_label(item = {}) {
    const case_item = item?.case_item || {};
    return this.first_defined_value([
      case_item?.workflow_display_stage,
      case_item?.current_workflow_status,
      case_item?.workflow_status_name,
      case_item?.workflow_status,
      case_item?.stage,
      case_item?.detail,
      case_item?.activation,
      case_item?.application_status,
      case_item?.status?.status,
      case_item?.status?.name,
      case_item?.status_name,
      case_item?.status,
      item?.gate
    ], '');
  },
  queue_status_tone(item = {}) {
    const label = String(this.queue_status_label(item) || '').trim().toLowerCase();
    const status = String(item?.status || '').trim().toLowerCase();
    const matched_rule = QUEUE_STATUS_TONE_RULES.find((rule) => rule.matches({ label, status }));
    return matched_rule?.tone || 'not-assessed';
  },
  activation_badge_class(tone) {
    if (tone === 'ready') return 'bg-success-subtle text-success-emphasis';
    if (tone === 'blocked') return 'bg-danger-subtle text-danger-emphasis';
    if (tone === 'pending') return 'bg-warning-subtle text-warning-emphasis';
    return 'bg-info-subtle text-info-emphasis';
  },
  answer_dot_class(tone) {
    if (tone === 'success') return 'bg-success';
    if (tone === 'warning') return 'bg-warning';
    return 'bg-secondary';
  }
};
